import React, { useEffect, useState } from "react";
import {
  FaBoxOpen,
  FaCheckCircle,
  FaClock,
  FaLock,
  FaTruck,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { getOrdersApi } from "../services/api";
import { useAuthStore } from "../store/authStore";
import Layout from "./Layout";
import ProfileSidebar from "./ProfileSidebar";

export default function MyOrders() {
  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // =====================================================
  // FETCH ORDERS
  // =====================================================

  useEffect(() => {
    let isMounted = true;

    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError("");

        const userId = user?.id;

        if (!userId) {
          if (isMounted) {
            setError("Please login to view your orders.");
            setLoading(false);
          }
          return;
        }

        const res = await getOrdersApi(userId);

        console.log("ORDERS API RESPONSE:", res);

        if (!isMounted) return;

        // If API returns { success, orders }
        if (res?.success) {
          setOrders(res.orders || []);
          return;
        }

        // If API directly returns an array
        if (Array.isArray(res)) {
          setOrders(res);
          return;
        }

        setError(res?.message || "Failed to load orders");
      } catch (err) {
        console.error("Fetch orders error:", err);

        // Don't allow an old request to overwrite a successful request
        if (!isMounted) return;

        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load your orders.",
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  // =====================================================
  // DATE FORMAT
  // =====================================================

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // =====================================================
  // STATUS
  // =====================================================

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "bg-green-100 text-green-700";

      case "shipped":
        return "bg-blue-100 text-blue-700";

      case "processing":
        return "bg-yellow-100 text-yellow-700";

      case "cancelled":
        return "bg-red-100 text-red-700";

      case "pending":
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // =====================================================
  // STATUS ICON
  // =====================================================

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return <FaCheckCircle />;

      case "shipped":
        return <FaTruck />;

      case "processing":
        return <FaClock />;

      default:
        return <FaBoxOpen />;
    }
  };

  // =====================================================
  // ERROR
  // =====================================================

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 text-center">
          {error}
        </div>
      </div>
    );
  }

  // =====================================================
  // ORDERS
  // =====================================================
  return (
    <Layout>
      {/* =========================
        COMMON HEADER
    ========================= */}
      <section className="bg-[#FFF8EC] px-6 md:px-16 py-8">
        <div className="mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center">
              <FaLock className="text-2xl text-[#FF945B]" />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-serif text-light_primary">
                My Orders
              </h2>

              <p className="text-gray-500 mt-1">Track and view your orders</p>
            </div>
          </div>
        </div>
      </section>

      {/* =========================
        COMMON CONTENT + SIDEBAR
    ========================= */}
      <section className="px-6 lg:px-12 md:px-12 sm:px-12 xs:px-10 xxs:px-8 py-6 md:py-10 lg:py-10 sm:py-10 xs:py-8 xxs:py-6 mx-auto bg-white">
        <div className="mx-auto grid grid-cols-1 md:grid-cols-[270px_1fr] gap-8">
          {/* SIDEBAR */}
          <ProfileSidebar />

          {/* RIGHT CONTENT */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8">
            {/* LOADING */}
            {loading && (
              <div className="text-center py-20 text-gray-500">
                Loading your orders...
              </div>
            )}

            {/* ERROR */}
            {!loading && error && (
              <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 text-center">
                {error}
              </div>
            )}

            {/* NO ORDERS */}
            {!loading && !error && !orders.length && (
              <div className="p-10 text-center">
                <FaBoxOpen className="mx-auto text-5xl text-gray-300 mb-4" />

                <h2 className="text-2xl font-semibold text-gray-800">
                  No Orders Yet
                </h2>

                <p className="text-gray-500 mt-2 mb-6">
                  You haven't placed any orders yet.
                </p>

                <button
                  onClick={() => navigate("/")}
                  className="bg-light_primary hover:bg-hover_light_primary text-white px-6 py-3 rounded-lg transition"
                >
                  Continue Shopping
                </button>
              </div>
            )}

            {/* ORDERS */}
            {!loading && !error && orders.length > 0 && (
              <>
                <h3 className="text-2xl font-semibold text-gray-900 border-b border-gray-200 pb-5 mb-6">
                  My Orders
                </h3>

                <div className="space-y-6">
                  {orders.map((order) => (
                    <div
                      key={order._id}
                      className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                    >
                      {/* ORDER HEADER */}
                      <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div>
                            <p className="text-sm text-gray-500">Order ID</p>

                            <p className="font-semibold text-gray-800">
                              #{order._id}
                            </p>
                          </div>

                          <div>
                            <p className="text-sm text-gray-500">Order Date</p>

                            <p className="font-medium text-gray-800">
                              {formatDate(order.createdAt)}
                            </p>
                          </div>

                          <div>
                            <p className="text-sm text-gray-500">Total</p>

                            <p className="font-semibold text-light_primary">
                              ₹
                              {Number(order.amount || 0).toLocaleString(
                                "en-IN",
                              )}
                            </p>
                          </div>

                          <div
                            className={`flex items-center gap-2 w-fit px-3 py-2 rounded-full text-sm font-medium ${getStatusClass(
                              order.status,
                            )}`}
                          >
                            {getStatusIcon(order.status)}

                            <span>{order.status || "Pending"}</span>
                          </div>

                          <div>
                            {order.order_status?.toLowerCase() ===
                            "delivered" ? (
                              <div>
                                <button
                                  onClick={() =>
                                    navigate(`/return-order/${order._id}`)
                                  }
                                  className="px-5 py-1.5 bg-light_primary hover:bg-hover_light_primary text-white rounded-lg font-medium transition"
                                >
                                  Return Order
                                </button>
                              </div>
                            ) : (
                              <div className="px-5 py-1.5 rounded-lg bg-gray-100 text-gray-700 font-medium">
                                {order.order_status
                                  ? order.order_status
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (char) =>
                                        char.toUpperCase(),
                                      )
                                  : "N/A"}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* PRODUCTS */}
                      <div className="p-5">
                        <div className="space-y-4">
                          {order.items?.map((item, index) => (
                            <div
                              key={`${item.productId}-${index}`}
                              className="flex gap-4 items-center"
                            >
                              {/* IMAGE */}
                              <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200">
                                {item.image?.[0] ? (
                                  <img
                                    src={item.image[0]}
                                    alt={item.title}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                    <FaBoxOpen className="text-gray-400" />
                                  </div>
                                )}
                              </div>

                              {/* DETAILS */}
                              <div className="flex-1">
                                <h3 className="font-medium text-gray-800">
                                  {item.title}
                                </h3>

                                <p className="text-sm text-gray-500 mt-1">
                                  ₹
                                  {Number(item.price || 0).toLocaleString(
                                    "en-IN",
                                  )}
                                  {" × "}
                                  {item.quantity}
                                </p>
                              </div>

                              {/* ITEM TOTAL */}
                              <div className="font-semibold text-gray-800">
                                ₹
                                {Number(
                                  item.price * item.quantity || 0,
                                ).toLocaleString("en-IN")}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
